import { useEffect } from "react";
import type { Editor } from "@tiptap/react";

/**
 * Keeps the editor usable with the on-screen keyboard open.
 *
 * Android Chrome resizes the layout viewport (interactive-widget=resizes-content in
 * index.html), so the caret is scrolled into view natively. iOS Safari only shrinks the
 * visual viewport, which can leave the caret and the status line under the keyboard.
 * This publishes the keyboard height as --kb (the status line sits above it) and
 * scrolls the caret back into the visible area after each edit or caret move.
 * Event listeners only: nothing runs while she isn't typing.
 */
export function useKeyboardAwareCaret(editor: Editor | null) {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const inset = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
      document.documentElement.style.setProperty("--kb", `${inset}px`);
    };
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      document.documentElement.style.removeProperty("--kb");
    };
  }, []);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!editor || !vv) return;
    let frame = 0;
    const reveal = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (!editor.isFocused || editor.isDestroyed) return;
        const caret = editor.view.coordsAtPos(editor.state.selection.head);
        const statusLine = document.querySelector(".status-line")?.getBoundingClientRect().height ?? 0;
        const top = vv.offsetTop + 8;
        const bottom = vv.offsetTop + vv.height - statusLine - 16;
        if (caret.bottom > bottom) window.scrollBy(0, caret.bottom - bottom);
        else if (caret.top < top) window.scrollBy(0, caret.top - top);
      });
    };
    editor.on("selectionUpdate", reveal);
    vv.addEventListener("resize", reveal);
    return () => {
      cancelAnimationFrame(frame);
      editor.off("selectionUpdate", reveal);
      vv.removeEventListener("resize", reveal);
    };
  }, [editor]);
}
